/**
 * Version File Name Cell Component
 *
 * Renders the file name column for version tables.
 * Displays file name with icon and system tags (current/previous).
 */

import React from 'react';
import { Files } from 'lucide-react';
import { LabelBadge } from '@loader/ui';

import type { Version } from '@/features/tables/types';

interface VersionFileNameCellProps {
  /** Version object containing file_name and system_tag */
  version: Version;
}

/**
 * Version File Name Cell Component
 *
 * Displays version file name with an icon and system tag badge.
 * System tag (current or previous) is displayed below the file name.
 */
export const VersionFileNameCell: React.FC<VersionFileNameCellProps> = ({ version }) => {
  const hasSystemTag = version.system_tag !== null;

  return (
    <div className="flex items-center gap-3">
      <div className="bg-green-500/10 rounded-lg p-2 shrink-0">
        <Files className="h-5 w-5 text-green-600 dark:text-green-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{version.file_name}</div>
        {hasSystemTag && (
          <div className="flex items-center gap-1 mt-1">
            <LabelBadge label={version.system_tag as 'current' | 'previous'} />
          </div>
        )}
      </div>
    </div>
  );
};
